import type { OwnerSectionKind, PageArrangedSection, PageArrangement, PageSectionRef } from './page-arrangement-api';

/**
 * A page's game lists as the owner arranges them: which lists the page shows, in what order, and which of
 * them are hidden. This is the owner's whole view of a page, and it is deliberately smaller than the model
 * underneath it.
 *
 * Underneath, a page is an ARRANGEMENT of sections (see `page-arrangement-api`): the platform's sections in
 * the brand's order, some hidden, plus sections the brand added for its own lists. Most sections show a
 * game list; the few that do not (the providers strip, a banner, search) are not the owner's to move. They
 * keep the place they have and every list moves around them, so the owner only ever deals in lists.
 *
 * Pure helpers only. They mirror the rules the backend applies when it builds the page
 * (`BuildPageSectionsDefinition` in spark-creatives), because what the owner is shown has to be the page
 * that publishing will produce.
 */

/** One of the brand's own lists on this page: the source a page renders it from, its name (and that name in each
 *  website language, when it has them), and its shape (a carousel when it does not say) with, for a grid, its
 *  layout. */
export type PageOwnList = {
  sourceId: string;
  name: string;
  names?: Record<string, string> | null;
  kind?: OwnerSectionKind;
  layout?: string;
};

/** A list's section title: its name in each website language it has, English always set (the site's fallback; the
 *  name itself when there is no English one). */
const ownListTitle = (list: PageOwnList): Record<string, string> => ({
  ...list.names,
  en: list.names?.en || list.name,
});

/** One list on the page, in page order. `listId` is the data source the page renders for it (for one of
 *  the brand's own lists, its `source_id`). The same list can be on a page twice, so `key` counts. */
export type PageListRow = { key: string; listId: string; own: boolean; hidden: boolean };

/** The handle the backend keys a platform section by (`SectionRef.Key`). */
const refKey = (ref: PageSectionRef): string => (ref.ds_id ? `ds:${ref.ds_id}` : `type:${ref.type}#${ref.nth}`);

/** The list a row shows, or null for a section that shows none. */
const rowList = (row: PageArrangedSection): { listId: string; own: boolean } | null => {
  if (row.owner) return { listId: row.owner.list_id, own: true };
  if (row.platform?.ds_id) return { listId: row.platform.ds_id, own: false };
  return null;
};

/**
 * The page an arrangement makes today, section by section, the way the backend resolves it at publish.
 *
 * A platform section is matched by its key, a repeated key taking the next section under it. One the page
 * no longer has is dropped (the platform removed it), and one the arrangement never mentions is added at
 * the end, visible (the platform added it since). An empty arrangement is therefore the platform's own page.
 *
 * `anchored` is a category page's rule (its links, as spark-creatives' BuildNavigationDefinition places them): a
 * link the arrangement never mentions keeps its place instead, right after the shown link that came before it in
 * the platform's order, or first when none did. An arrangement naming only the owner's lists (all a category page
 * could hold before its links were offered) so reads as the site shows it: the site's lists, then the owner's.
 */
export const resolveArrangement = (
  platform: PageSectionRef[],
  arrangement: PageArrangement,
  anchored = false,
): PageArrangement => {
  const byKey = new Map<string, number[]>();
  platform.forEach((ref, i) => byKey.set(refKey(ref), [...(byKey.get(refKey(ref)) ?? []), i]));
  const used = new Set<number>();
  const shown = new Set<number>();
  const order: PageArrangedSection[] = [];
  // The platform section each row of `order` is, -1 for one of the owner's.
  const at: number[] = [];
  for (const row of arrangement.order) {
    if (row.owner) {
      order.push({ owner: row.owner, hidden: row.hidden });
      at.push(-1);
      continue;
    }
    if (!row.platform) continue;
    const i = (byKey.get(refKey(row.platform)) ?? []).find((j) => !used.has(j));
    if (i === undefined) continue;
    used.add(i);
    if (!row.hidden) shown.add(i);
    order.push({ platform: platform[i], hidden: row.hidden });
    at.push(i);
  }
  if (!anchored) {
    platform.forEach((ref, i) => {
      if (!used.has(i)) order.push({ platform: ref, hidden: false });
    });
    return { order };
  }
  const leading: PageArrangedSection[] = [];
  const after = new Map<number, PageArrangedSection[]>();
  let anchor = -1;
  platform.forEach((ref, i) => {
    if (!used.has(i)) {
      const row = { platform: ref, hidden: false };
      if (anchor < 0) leading.push(row);
      else after.set(anchor, [...(after.get(anchor) ?? []), row]);
    } else if (shown.has(i)) anchor = i;
  });
  return {
    order: [...leading, ...order.flatMap((row, k) => [row, ...(at[k] >= 0 ? (after.get(at[k]) ?? []) : [])])],
  };
};

/** Where the lists sit in an arrangement: the index of every row that shows one, in page order. */
const listSlots = (arrangement: PageArrangement): number[] =>
  arrangement.order.flatMap((row, i) => (rowList(row) ? [i] : []));

/** The lists on the page, top to bottom, hidden ones included. */
export const pageListRows = (arrangement: PageArrangement): PageListRow[] => {
  const seen = new Map<string, number>();
  return listSlots(arrangement).map((i) => {
    const row = arrangement.order[i];
    const list = rowList(row) as { listId: string; own: boolean };
    const n = (seen.get(list.listId) ?? 0) + 1;
    seen.set(list.listId, n);
    return { key: `${list.listId}#${n}`, listId: list.listId, own: list.own, hidden: row.hidden };
  });
};

/**
 * The arrangement with the list at `from` moved to `to`, both counted among the page's lists.
 *
 * Only that list changes place: moving up, it goes in just before the list now at `to`; moving down, just
 * after it. Every other section keeps its neighbours, so a section that shows no list stays between the
 * same lists. Permuting the lists among the places lists already held looked equivalent and is not: on
 * dev-iulian, moving the owner's list to the top shifted every list above its old place down by one, and
 * pushed Kaboom Games below the providers strip.
 *
 * An index off the list, or a move in place, returns the arrangement unchanged.
 */
export const withListMoved = (arrangement: PageArrangement, from: number, to: number): PageArrangement => {
  const slots = listSlots(arrangement);
  if (from === to || from < 0 || to < 0 || from >= slots.length || to >= slots.length) return arrangement;
  const moved = arrangement.order[slots[from]];
  const target = arrangement.order[slots[to]];
  const order = arrangement.order.filter((_, i) => i !== slots[from]);
  const at = order.indexOf(target);
  order.splice(to < from ? at : at + 1, 0, moved);
  return { order };
};

/**
 * The move that puts `moved` where a new order of the page's lists has it, as `withListMoved` takes it: the
 * chips are dragged into `next`, and the page follows. `rows` are the page's lists in page order and `next`
 * the chips' lists in their new order. A chip the page does not place (the site reports its list, no section
 * shows it) is not in `rows`: the list goes in just after the nearest placed list before it in `next`, else
 * just before the nearest after it. Null when that is where it already is. Reorder lists' moves are made through it
 * too (`listDrags`), where a list the page has lost since is passed over the same way.
 */
export const listMoveFromOrder = (
  rows: readonly string[],
  next: readonly string[],
  moved: string,
): { from: number; to: number } | null => {
  const from = rows.indexOf(moved);
  const at = next.indexOf(moved);
  if (from === -1 || at === -1) return null;
  const placed = (id: string): boolean => id !== moved && rows.includes(id);
  const before = next.slice(0, at).reverse().find(placed);
  const after = next.slice(at + 1).find(placed);
  let to: number;
  if (before !== undefined) {
    const i = rows.indexOf(before);
    to = i < from ? i + 1 : i;
  } else if (after !== undefined) {
    const i = rows.indexOf(after);
    to = i < from ? i : i - 1;
  } else {
    return null;
  }
  return to === from ? null : { from, to };
};

/** The arrangement with the list at `index` (among the page's lists) hidden or shown. It keeps its place. */
export const withListHidden = (arrangement: PageArrangement, index: number, hidden: boolean): PageArrangement => {
  const slot = listSlots(arrangement)[index];
  if (slot === undefined || arrangement.order[slot].hidden === hidden) return arrangement;
  const order = arrangement.order.slice();
  order[slot] = { ...order[slot], hidden };
  return { order };
};

/**
 * One move made in Reorder lists, kept by the lists' keys (as `pageListRows` keys them) rather than their places, so it
 * still means the same move once rows come and go: the list `key`, and the keys in the order the popup showed them
 * once it had moved.
 */
export type ListMove = { key: string; order: readonly string[] };

/**
 * The drags (as `withListMoved` takes them) that make `moves` in turn on the lists keyed `keys` (`pageListRows`, in
 * page order), and the keys in the order they leave.
 *
 * Each list goes where its move put it, among the lists still there (`listMoveFromOrder`): just after the nearest list
 * that was above it, else just before the nearest that was below. So a move of a list the page has lost since makes no
 * drag, and a list the page has gained since is moved by none: it keeps its place, and the moved lists go in around it.
 */
export const listDrags = (
  keys: readonly string[],
  moves: readonly ListMove[],
): { drags: Array<{ from: number; to: number }>; keys: string[] } => {
  const order = keys.slice();
  const drags: Array<{ from: number; to: number }> = [];
  for (const move of moves) {
    const drag = listMoveFromOrder(order, move.order, move.key);
    if (!drag) continue;
    order.splice(drag.to, 0, ...order.splice(drag.from, 1));
    drags.push(drag);
  }
  return { drags, keys: order };
};

/**
 * Reorder lists' Save order, applied as ONE change: the owner's moves made in turn on the arrangement, each as the drag
 * it was (`withListMoved`, through `listDrags`), then each list hidden as `rows` says (keyed as `pageListRows` keys
 * them; a key the page no longer has is ignored).
 *
 * Made as the drags themselves, the moves put every section that shows no list exactly where the same drags in the
 * chips would. Laid out from the popup's final order instead (each list moved up to its place in turn), a list moved
 * down took the sections between its old and new place down with it, a change the popup, which shows lists only,
 * could not show. A list's order alone does not say where those sections go: Crash games moved up over New games and
 * New games moved down under Crash games give the same lists and two pages.
 */
export const withListRows = (
  arrangement: PageArrangement,
  rows: ReadonlyArray<{ key: string; hidden: boolean }>,
  moves: readonly ListMove[],
): PageArrangement => {
  const { drags, keys } = listDrags(
    pageListRows(arrangement).map((row) => row.key),
    moves,
  );
  let next = drags.reduce((made, { from, to }) => withListMoved(made, from, to), arrangement);
  // The flags go on by the key each row had when the popup showed it: `keys` follows the rows through the drags.
  // Read from `pageListRows` after the moves instead, the two rows of a list on the page twice would trade flags
  // whenever the owner swapped them, since it counts `#1` and `#2` afresh.
  const hiddenOf = new Map(rows.map((row) => [row.key, row.hidden]));
  keys.forEach((key, i) => {
    const hidden = hiddenOf.get(key);
    if (hidden !== undefined) next = withListHidden(next, i, hidden);
  });
  return next;
};

/**
 * The arrangement brought in line with the brand's own lists for this page.
 *
 * A list the brand has is on the page: one the arrangement does not have yet goes in before the first list,
 * visible (the newest first, as `own` gives them), so a list made with Add List heads the page's lists and
 * reaches the site at the next publish without a second step. A row
 * for a list the brand no longer has is dropped (the backend would refuse to publish it). Each row takes
 * the list's current name as its title and its current shape as its kind and layout, so a rename or a change
 * of shape reaches the site the same way. An empty layout is the site's plain grid.
 */
export const withOwnLists = (arrangement: PageArrangement, own: PageOwnList[]): PageArrangement => {
  const lists = new Map(own.map((list) => [list.sourceId, list]));
  const placed = new Set<string>();
  const order: PageArrangedSection[] = [];
  for (const row of arrangement.order) {
    if (!row.owner) {
      order.push(row);
      continue;
    }
    const list = lists.get(row.owner.list_id);
    if (list === undefined || placed.has(row.owner.list_id)) continue;
    placed.add(row.owner.list_id);
    const kind = list.kind ?? 'games_carousel';
    order.push({
      ...row,
      owner: {
        ...row.owner,
        title: ownListTitle(list),
        kind,
        // A layout is a grid's; a carousel carrying one would publish a change that changes nothing. A list that
        // does not say keeps the row's.
        layout: kind === 'games_grid' ? (list.layout ?? row.owner.layout) : '',
      },
    });
  }
  const added: PageArrangedSection[] = own
    .filter((list) => !placed.has(list.sourceId))
    .map((list) => ({
      owner: {
        list_id: list.sourceId,
        title: ownListTitle(list),
        kind: list.kind ?? 'games_carousel',
        layout: list.kind === 'games_grid' ? (list.layout ?? '') : '',
        link: '',
      },
      hidden: false,
    }));
  const slots = listSlots({ order });
  const before = slots.length > 0 ? slots[0] : order.length;
  return { order: [...order.slice(0, before), ...added, ...order.slice(before)] };
};

/** A row as the backend reads it, so two arrangements compare equal exactly when they publish the same page. */
const rowIdentity = (row: PageArrangedSection): string =>
  JSON.stringify(
    row.owner
      ? [
          'own',
          row.owner.list_id,
          Object.entries(row.owner.title).sort(([a], [b]) => a.localeCompare(b)),
          row.owner.kind,
          row.owner.layout,
          row.owner.link,
          row.hidden,
        ]
      : ['platform', row.platform ? refKey(row.platform) : '', row.hidden],
  );

/** Whether two RESOLVED arrangements make the same page. */
export const sameArrangement = (a: PageArrangement, b: PageArrangement): boolean =>
  a.order.length === b.order.length && a.order.every((row, i) => rowIdentity(row) === rowIdentity(b.order[i]));

/** A row's handle for matching the same section across two arrangements, counted so a repeat stays apart. */
const rowKeys = (arrangement: PageArrangement): string[] => {
  const seen = new Map<string, number>();
  return arrangement.order.map((row) => {
    const base = row.owner ? `own:${row.owner.list_id}` : row.platform ? refKey(row.platform) : '';
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return `${base}#${n}`;
  });
};

const showsList = (row: PageArrangedSection, listId: string): boolean => rowList(row)?.listId === listId;

/**
 * Whether the list itself moved between two RESOLVED arrangements. A list is where it was when the sections
 * around it are, on EITHER side: a neighbour moving away from it, or a list added after it, changes one side
 * only. Read by the section before it alone, a list whose neighbour moved counted as moved.
 */
const listMoved = (live: PageArrangement, working: PageArrangement, listId: string): boolean => {
  const sides = (arrangement: PageArrangement): Array<{ before: string; after: string }> => {
    const keys = rowKeys(arrangement);
    return arrangement.order.flatMap((row, i) =>
      showsList(row, listId) ? [{ before: i > 0 ? keys[i - 1] : '', after: i < keys.length - 1 ? keys[i + 1] : '' }] : [],
    );
  };
  const then = sides(live);
  const now = sides(working);
  return then.some((side, i) => side.before !== now[i]?.before && side.after !== now[i]?.after);
};

/** Whether the list's place on the page (where it is, whether it is hidden, its title) differs between two
 *  RESOLVED arrangements: one of the owner's own lists whose place waits to be published. */
export const listChanged = (live: PageArrangement, working: PageArrangement, listId: string): boolean => {
  const liveRows = live.order.filter((row) => showsList(row, listId));
  const workingRows = working.order.filter((row) => showsList(row, listId));
  if (liveRows.length !== workingRows.length) return true;
  if (liveRows.some((row, i) => rowIdentity(row) !== rowIdentity(workingRows[i]))) return true;
  return listMoved(live, working, listId);
};
